<template lang="pug">
  Field(
    :required="Boolean(required)"
    :label="label"
    :errors="!touched ? [] : errors"
    :for="labelInputAssociation || undefined"
    :class="$attrs.class"
    :style="$attrs.style"
    ref="root"
  )
    .input-wrap
      .show-password(
        v-if="type === 'password'"
        @click="showPassword = !showPassword"
      ) {{ !showPassword ? 'Show' : 'Hide' }}
      input(
        v-bind="bindAttrs"
        :name="name || undefined"
        :id="labelInputAssociation || undefined"
        :value="modelValue"
        :class="{\
            'input-touched': touched,\
            'input-error': errors.length > 0,\
            'has-left-icon': icon && iconPosition === 'left',\
            'has-right-icon': icon && iconPosition === 'right',\
            'has-clear-button': !!hasClearButton\
          }"
        :type="(type === 'password' && showPassword) ? 'text' : type",
        @reset="touched = false;"
        @input="update($event);"
        @blur="touched = true;"
        :maxlength="maxlength"
        :disabled="disabled"
        :placeholder="placeholder"
      )
    Icon.custom-icon(v-if="icon" :name="icon" fill="#333333" :class="iconPosition")
    button.clear(v-if="hasClearButton && modelValue" @click="$emit('update:modelValue', '');")
      Icon(name="x")
    slot(name="helper")
  </template>
  
<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue'
import { isEmail } from '../../js/validate-email'
  
defineOptions({
  inheritAttrs: false,
})
const attrs = useAttrs()
  
const props = defineProps({
  /**
     * The type of the form input field.
     * `text, number, password, email`
     */
  type: {
    type      : String,
    default   : 'text',
    validator : (value: any) => value.match(/(text|number|password|email|tel)/),
  },
  
  /**
     * Text value of the form input field.
     */
  modelValue: {
    type    : [Number, String],
    default : null,
  },
  
  /**
     * The label of the form input field.
     */
  label: {
    type    : String,
    default : '',
  },
  
  /**
     * Is the input required?
     */
  required: {
    type    : [Boolean, String],
    default : false,
  },
  
  /**
     * Function to validate input. Will be called with an argument of the value to validate
     * If the value is valid, return undefined
     * If the value is invalid, return a string that will be shown as the error
     */
  validateFn: {
    type    : Function,
    default : null,
  },

  loading: {
    type    : Boolean,
    default : false,
  },

  name: {
    type    : String,
    default : '',
  },

  id: {
    type    : String,
    default : '',
  },
  maxlength: {
    type    : Number,
    default : null,
  },
  disabled: {
    type    : Boolean,
    default : false,
  },
  placeholder: {
    type    : String,
    default : '',
  },
  icon: {
    type     : String,
    required : false,
  },
  iconPosition: {
    type    : String, // 'left', 'right'
    default : 'left',
  },
  hasClearButton: {
    type     : Boolean,
    required : false,
  },
})
  
const emit = defineEmits(['update:modelValue'])

const root = ref<HTMLElement | null>(null)
const showPassword = ref<boolean>(false)
const errors = ref<string[]>([])
const touched = ref<boolean>(false)
  
const bindAttrs = computed(() => {
  const {
    class : classAttr, style, label, validateFn, type, ...boundAttrs
  } = attrs
  
  return boundAttrs
})
  
const labelInputAssociation = computed<string>(() => {
  if (props.id) {
    return props.id
  }
  
  return props.name
})
  
function update(event: Event) {
  const target = event.target as HTMLInputElement
  emit(
    'update:modelValue',
    props.type === 'number' && Number.isNaN(target.value)
      ? null
      : target.value
  )
}
  
function validate() {
  const validationErrors: string[] = []
  const val = (props.modelValue || '').toString().trim()

  if (props.required && val.length === 0) {
    if (typeof props.required === 'string' && props.required !== 'true') {
      validationErrors.push(props.required)
    } else if (props.label) {
      validationErrors.push(`Please enter your ${props.label.toLowerCase()}`)
    } else {
      validationErrors.push('Required Field')
    }
  }
  
  if (props.validateFn) {
    const validationResult = props.validateFn(val)
    if (validationResult) {
      validationErrors.push(validationResult)
    }
  }
  
  if (props.type === 'email' && !isEmail(val)) {
    validationErrors.push('Please enter a valid email')
  }
  
  errors.value = validationErrors
}
  
watch(() => props.modelValue, validate, { immediate: true })

defineExpose({
  $el: root,
  // Lets a parent re-check this field when a value it validates against changes elsewhere
  // (e.g. a confirmation field after its source field is edited)
  validate,
})
</script>

<style lang="scss" scoped>

.input {
  width: 100%;
  position: relative;
  max-width: 100%;
}

// Wraps just the input so .show-password can centre on the input box itself
// rather than on the whole field (which also contains the label and errors).
.input-wrap {
  display: block;
  position: relative;
}

.show-password {
  position: absolute;
  right: $space-xs;
  top: 50%;
  transform: translateY(-50%);
  font-size: $size-xxs;
  cursor: pointer;
  user-select: none;
}

input {
  font-family: $font-sans-serif;
  font-size: var(--input-font-size, #{$size-xs});
  display: block;
  padding: var(--input-padding, #{$space-xs});
  border: var(--input-border, #{$border-width-thin} solid #{$color-gray});
  border-radius: var(--input-border-radius, 1px);
  width: var(--input-width, 100%);
  background: var(--input-background, #{$color-white});
  color: var(--input-color, revert);

  &.input-error.input-touched {
    border: $border-width-thin solid $color-red;
  }

  &:hover,
  &:focus {
    border: var(--input-border-focus, #{$border-width-thin} solid #{$color-gray-dark});
  }

  &[type="number"]::-webkit-outer-spin-button,
  &[type="number"]::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  &.has-left-icon {
    padding-left: 38px;
  }
  &.has-right-icon {
    padding-right: 38px;
  }

  &.has-clear-button {
    padding-right: 38px;
  }
}

@supports (-webkit-overflow-scrolling: touch) {
  input {
    font-size: var(--input-font-size-touch, #{$size-s});
  }
}

.custom-icon {
  position: absolute;
  top: $space-xs;

  &.left {
    left: $space-xs;
  }
  &.right {
    right: $space-xs;
  }
}

.clear {
  all: unset;
  cursor: pointer;
  position: absolute;
  right: $space-xs;
  top: $space-s;

  .icon {
    height: $size-xxs;
  }
}

</style>
